import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Button,
    Cell,
    Column,
    Row,
    Table,
    TableBody,
    TableHeader,
    Tree,
    TreeItem,
    TreeItemContent
} from 'react-aria-components';
import type { Key } from '@react-types/shared';
import { RefreshIcon } from '../../shared/icons';
import apiClient from '../../shared/api/apiClient';
import type { ClientCategoryDto, CategoryData } from '../CategoryManagement/types';
import type { TreeNode } from '../shared/TreeControl/types';
import type {
    IntegrationDiagnosticsDto,
    UnlinkedToMainProductsByCategoryDto,
    UnlinkedToMainProductsCategoryRowDto,
    UnlinkedToMainProductsProductDto
} from '../../types';
import './styles.css';

const CountCell: React.FC<{ value: number; accent?: boolean }> = ({ value, accent = false }) => (
    <td className="diagnostics-count-cell">
        <span className={value > 0 ? `diagnostics-count ${accent ? 'danger' : 'normal'}` : 'diagnostics-count empty'}>
            {value}
        </span>
    </td>
);

const getCategoryKey = (item: UnlinkedToMainProductsCategoryRowDto) =>
    item.supplierCategoryId === undefined || item.supplierCategoryId === null
        ? 'uncategorized'
        : `category-${item.supplierCategoryId}`;

const convertCategoryToTreeNode = (category: ClientCategoryDto): TreeNode<CategoryData> => ({
    id: category.id,
    name: category.name,
    children: [],
    data: {
        createdAt: category.createdAt,
        updatedAt: category.updatedAt,
        createdById: category.createdById,
        updatedById: category.updatedById,
        pricePercent: category.pricePercent,
        priceSum: category.priceSum,
        pricePercent2: category.pricePercent2,
        priceSum2: category.priceSum2
    },
    parentId: category.parentCategoryId
});

const buildCategoryTree = (categories: ClientCategoryDto[]): TreeNode<CategoryData>[] => {
    const categoryMap = new Map<number, TreeNode<CategoryData>>();
    const rootCategories: TreeNode<CategoryData>[] = [];

    categories.forEach(category => {
        categoryMap.set(category.id, convertCategoryToTreeNode(category));
    });

    categories.forEach(category => {
        const treeNode = categoryMap.get(category.id);
        if (!treeNode) {
            return;
        }

        if (category.parentCategoryId) {
            const parent = categoryMap.get(category.parentCategoryId);
            if (parent) {
                parent.children.push(treeNode);
            }
            return;
        }

        rootCategories.push(treeNode);
    });

    return rootCategories;
};

interface ClientCategoryTreePanelProps {
    categories: TreeNode<CategoryData>[];
    selectedCategoryId?: number;
    onCategorySelected: (node?: TreeNode<CategoryData>) => void;
}

const ClientCategoryTreePanel: React.FC<ClientCategoryTreePanelProps> = ({
    categories,
    selectedCategoryId,
    onCategorySelected
}) => {
    const [expandedKeys, setExpandedKeys] = useState<Set<Key>>(() => {
        try {
            const saved = localStorage.getItem('diagnostics_clientCategoryTreeExpanded');
            const ids = saved ? JSON.parse(saved) : [];
            return Array.isArray(ids) ? new Set(ids) : new Set();
        } catch (error) {
            console.warn('Failed to load diagnostics category tree expansion state:', error);
            return new Set();
        }
    });

    const nodeById = useMemo(() => {
        const map = new Map<number, TreeNode<CategoryData>>();
        const visit = (nodes: TreeNode<CategoryData>[]) => {
            for (const node of nodes) {
                map.set(node.id, node);
                visit(node.children);
            }
        };

        visit(categories);
        return map;
    }, [categories]);

    const handleSelectionChange = useCallback((keys: 'all' | Set<Key>) => {
        if (keys === 'all') {
            return;
        }

        const [selectedKey] = Array.from(keys);
        const selectedNode = selectedKey === undefined ? undefined : nodeById.get(Number(selectedKey));
        onCategorySelected(selectedNode);
    }, [nodeById, onCategorySelected]);

    const toggleCategoryExpansion = useCallback((nodeId: number) => {
        setExpandedKeys(current => {
            const next = new Set(current);
            if (next.has(nodeId)) {
                next.delete(nodeId);
            } else {
                next.add(nodeId);
            }

            return next;
        });
    }, []);

    const renderCategory = (node: TreeNode<CategoryData>, level = 0): React.ReactNode => {
        const hasChildren = node.children.length > 0;
        const isNodeExpanded = expandedKeys.has(node.id);
        const isSelected = selectedCategoryId === node.id;

        return (
            <TreeItem
                key={node.id}
                id={node.id}
                textValue={node.name}
                value={node}
                className="diagnostics-client-category-tree-item"
            >
                <TreeItemContent>
                    {({ isExpanded }) => (
                        <div
                            className={`diagnostics-client-category-tree-row ${isSelected ? 'selected' : ''}`}
                            style={{ '--tree-level': level } as React.CSSProperties}
                            onClick={(event) => {
                                if (!hasChildren || (event.target instanceof Element && event.target.closest('button'))) {
                                    return;
                                }

                                toggleCategoryExpansion(node.id);
                            }}
                        >
                            {hasChildren ? (
                                <Button slot="chevron" className="diagnostics-client-category-tree-toggle">
                                    <span className="diagnostics-client-category-tree-chevron">
                                        {isExpanded ? '▾' : '▸'}
                                    </span>
                                </Button>
                            ) : (
                                <span className="diagnostics-client-category-tree-placeholder" aria-hidden="true" />
                            )}

                            <span className="diagnostics-client-category-tree-name">{node.name}</span>
                        </div>
                    )}
                </TreeItemContent>

                {hasChildren && isNodeExpanded && node.children.map(child => renderCategory(child, level + 1))}
            </TreeItem>
        );
    };

    return (
        <Tree
            aria-label="Категории Лира"
            className="diagnostics-client-category-tree"
            expandedKeys={expandedKeys}
            onExpandedChange={setExpandedKeys}
            selectedKeys={selectedCategoryId === undefined ? [] : [selectedCategoryId]}
            onSelectionChange={handleSelectionChange}
            selectionMode="single"
            selectionBehavior="toggle"
        >
            {categories.map(category => renderCategory(category))}
        </Tree>
    );
};

interface BindingPanelProps {
    selectedSupplierCategory: UnlinkedToMainProductsCategoryRowDto;
    clientCategories: TreeNode<CategoryData>[];
    clientCategoriesLoading: boolean;
    clientCategoriesError: string | null;
    bindingCategory: boolean;
    bindingError: string | null;
    onBind: (clientCategoryId: number) => Promise<void>;
}

const BindingPanel: React.FC<BindingPanelProps> = ({
    selectedSupplierCategory,
    clientCategories,
    clientCategoriesLoading,
    clientCategoriesError,
    bindingCategory,
    bindingError,
    onBind
}) => {
    const [selectedClientCategory, setSelectedClientCategory] = useState<TreeNode<CategoryData> | undefined>(undefined);

    const handleSubmit = useCallback(() => {
        if (!selectedClientCategory || bindingCategory) {
            return;
        }

        void onBind(selectedClientCategory.id);
    }, [bindingCategory, onBind, selectedClientCategory]);

    return (
        <aside className="diagnostics-binding-panel" aria-label="Привязка к категории Лира">
            <div className="diagnostics-binding-panel-header">
                <h5>Категории Лира</h5>
                <p>{selectedSupplierCategory.supplierCategoryName}</p>
            </div>

            <div className="diagnostics-binding-panel-body">
                {clientCategoriesLoading && (
                    <div className="diagnostics-binding-state">
                        <span className="btn-spinner"></span>
                        <span>Загрузка...</span>
                    </div>
                )}

                {!clientCategoriesLoading && clientCategoriesError && (
                    <div className="diagnostics-binding-state error">{clientCategoriesError}</div>
                )}

                {!clientCategoriesLoading && !clientCategoriesError && clientCategories.length === 0 && (
                    <div className="diagnostics-binding-state">Нет категорий для отображения</div>
                )}

                {!clientCategoriesLoading && !clientCategoriesError && clientCategories.length > 0 && (
                    <ClientCategoryTreePanel
                        categories={clientCategories}
                        selectedCategoryId={selectedClientCategory?.id}
                        onCategorySelected={setSelectedClientCategory}
                    />
                )}
            </div>

            {bindingError && (
                <div className="diagnostics-binding-error">{bindingError}</div>
            )}

            <button
                type="button"
                className="diagnostics-binding-submit"
                disabled={!selectedClientCategory || bindingCategory}
                onClick={handleSubmit}
            >
                {bindingCategory ? 'Привязка...' : 'Привязать'}
            </button>
        </aside>
    );
};

const DiagnosticsManagement: React.FC = () => {
    const [rows, setRows] = useState<IntegrationDiagnosticsDto[]>([]);
    const [loading, setLoading] = useState(false);
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [detailsError, setDetailsError] = useState<string | null>(null);
    const [details, setDetails] = useState<UnlinkedToMainProductsByCategoryDto | null>(null);
    const [expandedCategoryKeys, setExpandedCategoryKeys] = useState<Set<string>>(new Set());
    const [productsByCategoryKey, setProductsByCategoryKey] = useState<Record<string, UnlinkedToMainProductsProductDto[]>>({});
    const [loadingCategoryKeys, setLoadingCategoryKeys] = useState<Set<string>>(new Set());
    const [categoryErrors, setCategoryErrors] = useState<Record<string, string>>({});
    const [clientCategories, setClientCategories] = useState<TreeNode<CategoryData>[]>([]);
    const [clientCategoriesLoading, setClientCategoriesLoading] = useState(false);
    const [clientCategoriesError, setClientCategoriesError] = useState<string | null>(null);
    const [selectedSupplierCategory, setSelectedSupplierCategory] = useState<UnlinkedToMainProductsCategoryRowDto | null>(null);
    const [bindingCategory, setBindingCategory] = useState(false);
    const [bindingError, setBindingError] = useState<string | null>(null);

    const loadDiagnostics = useCallback(async () => {
        setLoading(true);

        try {
            const response = await apiClient.get<IntegrationDiagnosticsDto[]>('/api/diagnostics');
            setRows(response.data);
        } finally {
            setLoading(false);
        }
    }, []);

    const loadClientCategories = useCallback(async () => {
        setClientCategoriesLoading(true);
        setClientCategoriesError(null);

        try {
            const response = await apiClient.get<ClientCategoryDto[]>('api/client-categories');
            setClientCategories(buildCategoryTree(response.data || []));
        } catch (error) {
            console.error('Error loading client categories:', error);
            setClientCategoriesError('Не удалось загрузить категории Лира.');
        } finally {
            setClientCategoriesLoading(false);
        }
    }, []);

    const closeDetails = useCallback(() => {
        setDetailsOpen(false);
        setDetailsLoading(false);
        setDetailsError(null);
        setDetails(null);
        setExpandedCategoryKeys(new Set());
        setProductsByCategoryKey({});
        setLoadingCategoryKeys(new Set());
        setCategoryErrors({});
        setSelectedSupplierCategory(null);
        setBindingCategory(false);
        setBindingError(null);
    }, []);

    const openUnlinkedDetails = useCallback(async (row: IntegrationDiagnosticsDto) => {
        if (row.unlinkedToMainProductsCount <= 0) {
            return;
        }

        setDetailsOpen(true);
        setDetailsLoading(true);
        setDetailsError(null);
        setDetails(null);
        setExpandedCategoryKeys(new Set());
        setProductsByCategoryKey({});
        setLoadingCategoryKeys(new Set());
        setCategoryErrors({});
        setSelectedSupplierCategory(null);
        setBindingError(null);

        try {
            const response = await apiClient.get<UnlinkedToMainProductsByCategoryDto>(
                `/api/diagnostics/unlinked-to-main-products-by-category?integrationId=${row.integrationId}`
            );
            setDetails(response.data);
        } catch (error) {
            console.error('Error loading unlinked-to-main-products breakdown:', error);
            setDetailsError('Не удалось загрузить детализацию.');
        } finally {
            setDetailsLoading(false);
        }
    }, []);

    const toggleCategoryProducts = useCallback(async (item: UnlinkedToMainProductsCategoryRowDto) => {
        if (!details) {
            return;
        }

        const categoryKey = getCategoryKey(item);
        const isExpanded = expandedCategoryKeys.has(categoryKey);

        setExpandedCategoryKeys((current) => {
            const next = new Set(current);
            if (isExpanded) {
                next.delete(categoryKey);
            } else {
                next.add(categoryKey);
            }
            return next;
        });

        if (isExpanded || productsByCategoryKey[categoryKey] || loadingCategoryKeys.has(categoryKey)) {
            return;
        }

        setLoadingCategoryKeys((current) => new Set(current).add(categoryKey));
        setCategoryErrors((current) => {
            const next = { ...current };
            delete next[categoryKey];
            return next;
        });

        const categoryQuery = item.supplierCategoryId === undefined || item.supplierCategoryId === null
            ? ''
            : `&supplierCategoryId=${item.supplierCategoryId}`;

        try {
            const response = await apiClient.get<UnlinkedToMainProductsProductDto[]>(
                `/api/diagnostics/unlinked-to-main-products-by-category-products?integrationId=${details.integrationId}${categoryQuery}`
            );

            setProductsByCategoryKey((current) => ({
                ...current,
                [categoryKey]: response.data
            }));
        } catch (error) {
            console.error('Error loading unlinked-to-main-products category products:', error);
            setCategoryErrors((current) => ({
                ...current,
                [categoryKey]: 'Не удалось загрузить позиции поставщика.'
            }));
        } finally {
            setLoadingCategoryKeys((current) => {
                const next = new Set(current);
                next.delete(categoryKey);
                return next;
            });
        }
    }, [details, expandedCategoryKeys, loadingCategoryKeys, productsByCategoryKey]);

    const handleStartBinding = useCallback((item: UnlinkedToMainProductsCategoryRowDto) => {
        if (!item.supplierCategoryId) {
            return;
        }

        setSelectedSupplierCategory(item);
        setBindingError(null);

        if (clientCategories.length === 0 && !clientCategoriesLoading) {
            void loadClientCategories();
        }
    }, [clientCategories.length, clientCategoriesLoading, loadClientCategories]);

    const handleBindSelectedCategory = useCallback(async (clientCategoryId: number) => {
        if (!selectedSupplierCategory?.supplierCategoryId || bindingCategory) {
            return;
        }

        const supplierCategoryId = selectedSupplierCategory.supplierCategoryId;
        const categoryKey = getCategoryKey(selectedSupplierCategory);

        setBindingCategory(true);
        setBindingError(null);

        try {
            await apiClient.post(
                `api/client-categories/${clientCategoryId}/mappings/${supplierCategoryId}`,
                {}
            );

            setDetails(current => current
                ? {
                    ...current,
                    rows: current.rows.filter(row => row.supplierCategoryId !== supplierCategoryId)
                }
                : current);

            setExpandedCategoryKeys(current => {
                const next = new Set(current);
                next.delete(categoryKey);
                return next;
            });

            setProductsByCategoryKey(current => {
                const next = { ...current };
                delete next[categoryKey];
                return next;
            });

            setSelectedSupplierCategory(null);
            await loadDiagnostics();
        } catch (error) {
            console.error('Error binding supplier category:', error);
            setBindingError(error instanceof Error ? error.message : 'Не удалось привязать категорию поставщика.');
        } finally {
            setBindingCategory(false);
        }
    }, [bindingCategory, loadDiagnostics, selectedSupplierCategory]);

    useEffect(() => {
        loadDiagnostics();
    }, [loadDiagnostics]);

    return (
        <div className="diagnostics-management container-fluid">
            <div className="filter-controls-compact">
                <div className="filter-controls-row">
                    <h3>Диагностика</h3>

                    <div className="filter-controls-right">
                        <button
                            className="btn-compact refresh"
                            onClick={loadDiagnostics}
                            disabled={loading}
                            title="Обновить"
                        >
                            {loading ? (
                                <span className="btn-spinner"></span>
                            ) : (
                                <RefreshIcon />
                            )}
                        </button>
                    </div>
                </div>
            </div>

            <div className="diagnostics-management-table-container">
                <table className="diagnostics-management-table">
                    <thead>
                        <tr>
                            <th>Интеграция</th>
                            <th>Поставщик</th>
                            <th className="text-end">Загружено Н</th>
                            <th className="text-end">Н без категории</th>
                            <th className="text-end">Н без привязки к НЛ</th>
                            <th className="text-end">Просроченные Н</th>
                            <th className="text-end">Н без цены</th>
                            <th className="text-end">Н без vendor</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => (
                            <tr key={row.integrationId}>
                                <td className="diagnostics-name-cell">{row.integrationName}</td>
                                <td>{row.supplierName}</td>
                                <CountCell value={row.loadedProductsCount} />
                                <CountCell value={row.withoutCategoryCount} accent />
                                <td className="diagnostics-count-cell">
                                    <button
                                        type="button"
                                        className={`diagnostics-count-button ${row.unlinkedToMainProductsCount > 0 ? 'interactive' : 'disabled'}`}
                                        onClick={() => openUnlinkedDetails(row)}
                                        disabled={row.unlinkedToMainProductsCount <= 0}
                                        title={row.unlinkedToMainProductsCount > 0 ? 'Показать категории поставщика' : undefined}
                                    >
                                        <span
                                            className={
                                                row.unlinkedToMainProductsCount > 0
                                                    ? 'diagnostics-count danger'
                                                    : 'diagnostics-count empty'
                                            }
                                        >
                                            {row.unlinkedToMainProductsCount}
                                        </span>
                                    </button>
                                </td>
                                <CountCell value={row.expiredProductsCount} accent />
                                <CountCell value={row.withoutPriceCount} accent />
                                <CountCell value={row.withoutVendorCount} accent />
                            </tr>
                        ))}
                        {loading && rows.length === 0 && (
                            <tr>
                                <td colSpan={8} className="diagnostics-empty-cell">Загрузка...</td>
                            </tr>
                        )}
                        {!loading && rows.length === 0 && (
                            <tr>
                                <td colSpan={8} className="diagnostics-empty-cell">Нет данных для отображения</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <div className="diagnostics-footer text-muted">
                Показано {rows.length} интеграций
            </div>

            {detailsOpen && (
                <div className="diagnostics-modal-backdrop" onClick={closeDetails}>
                    <div
                        className={`diagnostics-modal ${selectedSupplierCategory ? 'with-binding-panel' : ''}`}
                        onClick={(event) => event.stopPropagation()}
                    >
                        <div className="diagnostics-modal-header">
                            <div>
                                <h4>Категории поставщика без привязки к НЛ</h4>
                                {details && (
                                    <p>{details.integrationName}</p>
                                )}
                            </div>
                            <button
                                type="button"
                                className="diagnostics-modal-close"
                                onClick={closeDetails}
                                aria-label="Закрыть"
                            >
                                ×
                            </button>
                        </div>

                        <div className="diagnostics-modal-body">
                            {detailsLoading && (
                                <div className="diagnostics-modal-state">
                                    <span className="btn-spinner"></span>
                                    <span>Загрузка...</span>
                                </div>
                            )}

                            {!detailsLoading && detailsError && (
                                <div className="diagnostics-modal-state error">{detailsError}</div>
                            )}

                            {!detailsLoading && !detailsError && details && details.rows.length === 0 && (
                                <div className="diagnostics-modal-state">Нет данных для отображения</div>
                            )}

                            {!detailsLoading && !detailsError && details && details.rows.length > 0 && (
                                <div className={`diagnostics-binding-layout ${selectedSupplierCategory ? 'with-panel' : ''}`}>
                                    <div className="diagnostics-modal-table-container">
                                    <Table
                                        aria-label="Категории поставщика без привязки к НЛ"
                                        className="diagnostics-modal-table"
                                    >
                                        <TableHeader>
                                            <Column id="category" isRowHeader>
                                                Категория поставщика
                                            </Column>
                                            <Column id="count" className="diagnostics-count-column text-end">
                                                Количество
                                            </Column>
                                            <Column id="action" className="diagnostics-action-column text-end">
                                                Действие
                                            </Column>
                                        </TableHeader>
                                        <TableBody>
                                            {details.rows.flatMap((item) => {
                                                const categoryKey = getCategoryKey(item);
                                                const isExpanded = expandedCategoryKeys.has(categoryKey);
                                                const products = productsByCategoryKey[categoryKey];
                                                const isProductsLoading = loadingCategoryKeys.has(categoryKey);
                                                const categoryError = categoryErrors[categoryKey];

                                                if (!isExpanded) {
                                                    return [(
                                                        <Row
                                                            key={categoryKey}
                                                            id={categoryKey}
                                                            className="diagnostics-modal-category-row"
                                                        >
                                                            <Cell>
                                                                <button
                                                                    type="button"
                                                                    className="diagnostics-category-toggle"
                                                                    aria-expanded={false}
                                                                    onClick={() => toggleCategoryProducts(item)}
                                                                >
                                                                    <span className="diagnostics-category-chevron">▶</span>
                                                                    <span>{item.supplierCategoryName}</span>
                                                                </button>
                                                            </Cell>
                                                            <Cell className="diagnostics-count-column text-end">{item.count}</Cell>
                                                            <Cell className="diagnostics-action-column text-end">
                                                                {item.supplierCategoryId ? (
                                                                    <button
                                                                        type="button"
                                                                        className={`diagnostics-bind-button ${selectedSupplierCategory?.supplierCategoryId === item.supplierCategoryId ? 'active' : ''}`}
                                                                        onClick={() => handleStartBinding(item)}
                                                                    >
                                                                        Привязать
                                                                    </button>
                                                                ) : null}
                                                            </Cell>
                                                        </Row>
                                                    )];
                                                }

                                                return [
                                                    (
                                                    <Row
                                                        key={categoryKey}
                                                        id={categoryKey}
                                                        className="diagnostics-modal-category-row expanded"
                                                    >
                                                        <Cell>
                                                            <button
                                                                type="button"
                                                                className="diagnostics-category-toggle"
                                                                aria-expanded={true}
                                                                onClick={() => toggleCategoryProducts(item)}
                                                            >
                                                                <span className="diagnostics-category-chevron">▼</span>
                                                                <span>{item.supplierCategoryName}</span>
                                                            </button>
                                                        </Cell>
                                                        <Cell className="diagnostics-count-column text-end">{item.count}</Cell>
                                                        <Cell className="diagnostics-action-column text-end">
                                                            {item.supplierCategoryId ? (
                                                                <button
                                                                    type="button"
                                                                    className={`diagnostics-bind-button ${selectedSupplierCategory?.supplierCategoryId === item.supplierCategoryId ? 'active' : ''}`}
                                                                    onClick={() => handleStartBinding(item)}
                                                                >
                                                                    Привязать
                                                                </button>
                                                            ) : null}
                                                        </Cell>
                                                    </Row>
                                                    ),
                                                    (
                                                    <Row
                                                        key={`${categoryKey}-products`}
                                                        id={`${categoryKey}-products`}
                                                        className="diagnostics-modal-products-row"
                                                    >
                                                        <Cell colSpan={3}>
                                                            <div className="diagnostics-products-panel">
                                                                {isProductsLoading && (
                                                                    <div className="diagnostics-products-state">
                                                                        <span className="btn-spinner"></span>
                                                                        <span>Загрузка...</span>
                                                                    </div>
                                                                )}

                                                                {!isProductsLoading && categoryError && (
                                                                    <div className="diagnostics-products-state error">{categoryError}</div>
                                                                )}

                                                                {!isProductsLoading && !categoryError && products && products.length === 0 && (
                                                                    <div className="diagnostics-products-state">
                                                                        Нет позиций для отображения
                                                                    </div>
                                                                )}

                                                                {!isProductsLoading && !categoryError && products && products.length > 0 && (
                                                                    <Table
                                                                        aria-label={`Позиции поставщика: ${item.supplierCategoryName}`}
                                                                        className="diagnostics-products-table"
                                                                    >
                                                                        <TableHeader>
                                                                            <Column id="externalId">Идентификатор</Column>
                                                                            <Column id="name" isRowHeader>Наименование</Column>
                                                                            <Column id="sku">SKU</Column>
                                                                        </TableHeader>
                                                                        <TableBody>
                                                                            {products.map((product) => (
                                                                                <Row key={product.id} id={product.id}>
                                                                                    <Cell>{product.id}</Cell>
                                                                                    <Cell>{product.name}</Cell>
                                                                                    <Cell>{product.sku || '—'}</Cell>
                                                                                </Row>
                                                                            ))}
                                                                        </TableBody>
                                                                    </Table>
                                                                )}
                                                            </div>
                                                        </Cell>
                                                    </Row>
                                                    )
                                                ];
                                            })}
                                        </TableBody>
                                    </Table>
                                </div>
                                    {selectedSupplierCategory && (
                                        <BindingPanel
                                            key={selectedSupplierCategory.supplierCategoryId}
                                            selectedSupplierCategory={selectedSupplierCategory}
                                            clientCategories={clientCategories}
                                            clientCategoriesLoading={clientCategoriesLoading}
                                            clientCategoriesError={clientCategoriesError}
                                            bindingCategory={bindingCategory}
                                            bindingError={bindingError}
                                            onBind={handleBindSelectedCategory}
                                        />
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DiagnosticsManagement;
